<template>
  <div
    class="modal fade"
    id="modalRefund"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">
        <div class="modal-header border-0 pb-2">
          <h5 class="modal-title fw-bold text-danger">
            <i class="fa-solid fa-rotate-left me-2"/>
            {{ $t('Refund Bill') }}
            <span class="text-muted ms-1">#{{ bill.bill_no }}</span>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 pb-4">
          <div class="card bg-light border-0 rounded-3 mb-3">
            <div class="card-body p-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">{{ $t('Bill Total') }}</div>
                <div class="fs-5 fw-bold">
                  {{ $storeIndex.formatThaiCurrency(billTotal) }}
                </div>
                <div v-if="alreadyRefunded > 0" class="small mt-1">
                  <span class="text-muted">{{ $t('Already Refunded') }}:</span>
                  <span class="text-danger fw-semibold ms-1">
                    {{ $storeIndex.formatThaiCurrency(alreadyRefunded) }}
                  </span>
                </div>
              </div>
              <i class="fa-solid fa-circle-exclamation fa-2x text-warning"/>
            </div>
          </div>

          <div class="mb-3">
            <div class="text-muted small mb-2">
              {{ $t('Select items and quantities to refund') }}
            </div>
            <div class="table-responsive border rounded-3">
              <table class="table table-sm mb-0">
                <thead class="table-light small">
                  <tr>
                    <th style="width: 40px;"></th>
                    <th>{{ $t('Item') }}</th>
                    <th class="text-center" style="width: 120px;">{{ $t('Refundable') }}</th>
                    <th class="text-center" style="width: 150px;">{{ $t('Refund Qty') }}</th>
                    <th class="text-end" style="width: 120px;">{{ $t('Amount') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, i) in itemRows"
                    :key="row.id"
                    :class="{ 'table-active': row.qty > 0, 'text-muted': row.refundable === 0 }"
                  >
                    <td class="align-middle">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="row.qty > 0"
                        :disabled="row.refundable === 0"
                        @change="toggleItem(i, $event.target.checked)"
                      >
                    </td>
                    <td class="align-middle">
                      <div class="fw-semibold">{{ row.name }}</div>
                      <div class="text-muted small">
                        {{ $storeIndex.formatThaiCurrency(row.unitPrice) }} × {{ row.orderedQuantity }}
                      </div>
                      <div v-if="row.cancelledQuantity > 0" class="text-danger small">
                        {{ $t('Cancelled') }}: {{ row.cancelledQuantity }}
                      </div>
                      <div v-if="row.refundedQuantity > 0" class="text-warning small">
                        {{ $t('Refunded') }}: {{ row.refundedQuantity }}
                      </div>
                    </td>
                    <td class="text-center align-middle">
                      <span v-if="row.refundable < row.orderedQuantity" class="text-warning">
                        {{ row.refundable }} / {{ row.orderedQuantity }}
                      </span>
                      <span v-else>{{ row.refundable }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <div class="custom-input-group input-group input-group-sm qty-input-group flex-nowrap mx-auto">
                        <button
                          type="button"
                          class="input-group-text bg-white border-end-0 text-muted cursor-pointer"
                          :disabled="row.qty <= 0"
                          @click="setQty(i, row.qty - 1)"
                        >
                          <i class="fa-solid fa-minus"></i>
                        </button>
                        <input
                          type="number"
                          class="form-control border-start-0 border-end-0 text-center px-0"
                          :value="row.qty"
                          :min="0"
                          :max="row.refundable"
                          @input="setQty(i, Number($event.target.value))"
                        >
                        <button
                          type="button"
                          class="input-group-text bg-white border-start-0 text-muted cursor-pointer"
                          :disabled="row.qty >= row.refundable"
                          @click="setQty(i, row.qty + 1)"
                        >
                          <i class="fa-solid fa-plus"></i>
                        </button>
                      </div>
                    </td>
                    <td class="text-end align-middle">
                      {{ $storeIndex.formatThaiCurrency(rowAmount(row)) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card bg-light border-0 rounded-3 mt-2">
              <div class="card-body p-3 small">
                <div class="d-flex justify-content-between fw-bold">
                  <span>{{ $t('Total Refund') }}</span>
                  <span class="text-danger fs-5">
                    {{ $storeIndex.formatThaiCurrency(totalRefundAmount) }}
                  </span>
                </div>
                <div class="text-muted mt-1">
                  {{ $t('Includes proportional bill discount, service charge, and VAT based on item share of the bill.') }}
                </div>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold small">
              {{ $t('Refund Reason') }}
              <span class="text-danger">*</span>
            </label>
            <div class="custom-input-group custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
              <textarea
                class="form-control"
                rows="3"
                :placeholder="$t('Reason for refund') + '...'"
                v-model.trim="form.refund_reason"
                maxlength="500"
              />
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button
              type="button"
              class="btn btn-outline-secondary flex-grow-1 py-2 fw-bold rounded-3"
              data-bs-dismiss="modal"
            >
              {{ $t('Cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger flex-grow-1 py-2 fw-bold rounded-3"
              :disabled="!canConfirm || submitting"
              @click="confirm"
            >
              <i v-if="submitting" class="fa-solid fa-spinner fa-spin me-2"/>
              <i v-else class="fa-solid fa-rotate-left me-2"/>
              {{ $t('Confirm Refund') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ModalRefund',
  props: {
    bill: { type: Object, required: true }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      submitting: false,
      itemRows: [],
      form: {
        refund_reason: ''
      }
    };
  },
  computed: {
    billTotal() {
      return this.toNumber(this.bill.total);
    },
    billSubtotal() {
      return this.toNumber(this.bill.sub_total);
    },
    alreadyRefunded() {
      return this.toNumber(this.bill.refunded_amount);
    },
    // refund_per_item = (item.total / bill.sub_total) × bill.total × (refund_qty / ordered_qty)
    // distributes proportional discount/service/VAT across the refunded items.
    totalRefundAmount() {
      if (this.billSubtotal <= 0) return 0;
      return this.itemRows.reduce((sum, row) => sum + this.rowAmount(row), 0);
    },
    selectedItems() {
      return this.itemRows
        .filter(r => r.qty > 0)
        .map(r => ({ id: r.id, quantity: r.qty }));
    },
    canConfirm() {
      return this.selectedItems.length > 0
        && (this.form.refund_reason || '').trim().length > 0;
    }
  },
  mounted() {
    this.itemRows = this.buildItemRows();
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  },
  methods: {
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    buildItemRows() {
      return (this.bill.items || []).map(it => {
        const ordered   = this.toNumber(it.quantity);
        const cancelled = this.toNumber(it.cancelled_quantity);
        const refunded  = this.toNumber(it.refunded_quantity);
        const refundable = Math.max(0, ordered - cancelled - refunded);
        return {
          id: it.id,
          name: it.name,
          unitPrice: this.toNumber(it.unit_price),
          total: this.toNumber(it.total),
          orderedQuantity: ordered,
          cancelledQuantity: cancelled,
          refundedQuantity: refunded,
          refundable,
          qty: 0
        };
      });
    },
    rowAmount(row) {
      // Proportional refund — distributes bill-level discount/service/VAT
      // across the item by share of subtotal:
      //   refund = (item.total / bill.sub_total) × bill.total × (refund_qty / ordered_qty)
      if (row.orderedQuantity <= 0 || this.billSubtotal <= 0) return 0;
      const itemShare = row.total / this.billSubtotal;
      return itemShare * this.billTotal * (row.qty / row.orderedQuantity);
    },
    toggleItem(idx, checked) {
      const row = this.itemRows[idx];
      this.setQty(idx, checked ? row.refundable : 0);
    },
    setQty(idx, q) {
      const row = this.itemRows[idx];
      const clamped = Math.max(0, Math.min(row.refundable, Number(q) || 0));
      this.itemRows[idx] = { ...row, qty: clamped };
    },
    async confirm() {
      if (!this.canConfirm || this.submitting) return;
      this.submitting = true;
      this.$emit('confirm', {
        refund_reason: this.form.refund_reason.trim(),
        items: this.selectedItems
      });
    },
    hide() {
      $(this.$refs.modal).modal('hide');
    },
    stopSubmitting() {
      this.submitting = false;
    }
  }
};
</script>

<style scoped>
/* ช่องจำนวนคืน — ทรงเดียวกับ .custom-input-group หน้า profile */
.qty-input-group {
  width: 116px;
}

.qty-input-group .input-group-text {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.qty-input-group .input-group-text:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ซ่อนลูกศร spinner ของ input[type=number] ให้เหลือแต่ปุ่ม +/- */
.qty-input-group input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.qty-input-group input[type='number']::-webkit-outer-spin-button,
.qty-input-group input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
